"use client";

import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { type ConvexReactClient, ConvexProviderWithAuth } from "convex/react";
import { ConvexQueryCacheProvider } from "convex-helpers/react/cache";
import { createBrowserConvexClient } from "@/lib/convex/traced-client";
import {
  AuthKitProvider,
  useAuth,
  useAccessToken,
} from "@workos-inc/authkit-nextjs/components";
import { safeWebSocketConstructor } from "@/lib/safe-websocket";
import { addBreadcrumb } from "@/lib/error-tracking";
import { ReportIssueProvider } from "@/components/feedback/report-issue-provider";
import { GlobalErrorBanner } from "@/components/feedback/global-error-banner";
import { ViewAsBanner } from "@/components/view-as/view-as-banner";
import { deferredIsland } from "@/components/ui/deferred-island";
import { usePublicSessionPending } from "./use-public-session";
import {
  endProtectedConvexSession,
  type RefreshOutcome,
} from "./convex-session-end";

const AUTH_TOKEN_REFRESH_LEEWAY_SECONDS = 75;

const ErrorTrackingUserSync = deferredIsland(
  () =>
    import("./error-tracking-user-sync").then(
      (module) => module.ErrorTrackingUserSync,
    ),
  "error-tracking-user-sync",
);

const AnalyticsUserSync = deferredIsland(
  () => import("./analytics-user-sync").then((module) => module.AnalyticsUserSync),
  "analytics-user-sync",
);

const CartMerger = deferredIsland(
  () => import("@/components/cart/CartMerger").then((module) => module.CartMerger),
  "cart-merger",
);

function createConvexClient(expectAuth: boolean) {
  return createBrowserConvexClient(process.env.NEXT_PUBLIC_CONVEX_URL!, {
    webSocketConstructor: safeWebSocketConstructor(),
    expectAuth,
    authRefreshTokenLeewaySeconds: AUTH_TOKEN_REFRESH_LEEWAY_SECONDS,
    initialAuthTokenReuse: true,
  });
}

const clients: { public?: ConvexReactClient; protected?: ConvexReactClient } = {};

function convexClientFor(expectAuth: boolean): ConvexReactClient {
  const key = expectAuth ? "protected" : "public";
  clients[key] ??= createConvexClient(expectAuth);
  return clients[key];
}

export const REFRESH_RETRY_DELAYS_MS = [1_000, 3_000];

function recordTokenFetchFailure(error: unknown, forceRefreshToken: boolean) {
  addBreadcrumb({
    category: "auth",
    message: "fetchAccessToken failed",
    level: "warning",
    data: {
      forceRefreshToken,
      error: error instanceof Error ? error.message : String(error),
    },
  });
}

async function refreshWithRetries(
  refresh: () => Promise<string | undefined>,
): Promise<RefreshOutcome> {
  let failure: unknown;
  let attempts = 0;
  for (const retryDelayMs of [...REFRESH_RETRY_DELAYS_MS, null]) {
    attempts += 1;
    try {
      const token = await refresh();
      if (token) return { token, failure: undefined, attempts };
    } catch (error) {
      failure = error;
      recordTokenFetchFailure(error, true);
    }
    if (retryDelayMs === null) break;
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
  }
  return { token: null, failure, attempts };
}

type TokenRequest = { forceRefreshToken?: boolean };

function useAuthKitTokenSource() {
  const { user, loading: isLoading } = useAuth();
  const userId = user?.id;
  const { getAccessToken, accessToken, refresh } = useAccessToken();

  const accessTokenRef = useRef<string | undefined>(accessToken);
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const fetchCachedToken = useCallback(async (): Promise<string | null> => {
    if (userId === undefined) return null;
    try {
      return (await getAccessToken()) ?? null;
    } catch (error) {
      recordTokenFetchFailure(error, false);
      return accessTokenRef.current ?? null;
    }
  }, [userId, getAccessToken]);

  const refreshToken = useCallback(async (): Promise<RefreshOutcome> => {
    if (userId === undefined) return { token: null, failure: undefined, attempts: 0 };
    return refreshWithRetries(refresh);
  }, [userId, refresh]);

  return {
    isLoading: isLoading ?? false,
    isAuthenticated: userId !== undefined,
    fetchCachedToken,
    refreshToken,
  };
}

export function useAuthFromAuthKit() {
  const { isLoading, isAuthenticated, fetchCachedToken, refreshToken } =
    useAuthKitTokenSource();
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: TokenRequest = {}): Promise<string | null> =>
      forceRefreshToken ? (await refreshToken()).token : fetchCachedToken(),
    [fetchCachedToken, refreshToken],
  );
  return useMemo(
    () => ({ isLoading, isAuthenticated, fetchAccessToken }),
    [isLoading, isAuthenticated, fetchAccessToken],
  );
}

function useProtectedAuthFromAuthKit() {
  const { isLoading, isAuthenticated, fetchCachedToken, refreshToken } =
    useAuthKitTokenSource();
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: TokenRequest = {}): Promise<string | null> => {
      if (!forceRefreshToken) return fetchCachedToken();
      const client = convexClientFor(true);
      const socketWasConnected = client.connectionState().isWebSocketConnected;
      const outcome = await refreshToken();
      if (outcome.token !== null) return outcome.token;
      endProtectedConvexSession(client, { ...outcome, socketWasConnected });
      return new Promise<never>(() => {});
    },
    [fetchCachedToken, refreshToken],
  );
  return useMemo(
    () => ({ isLoading, isAuthenticated, fetchAccessToken }),
    [isLoading, isAuthenticated, fetchAccessToken],
  );
}

function usePublicAuthFromAuthKit() {
  const auth = useAuthFromAuthKit();
  const isSessionPending = usePublicSessionPending();
  return useMemo(
    () => ({ ...auth, isLoading: auth.isLoading || isSessionPending }),
    [auth, isSessionPending],
  );
}

function authHookFor(isPublic: boolean, expectAuth: boolean) {
  if (isPublic) return usePublicAuthFromAuthKit;
  return expectAuth ? useProtectedAuthFromAuthKit : useAuthFromAuthKit;
}

type InitialAuth = ComponentProps<typeof AuthKitProvider>["initialAuth"];

const SIGNED_OUT: InitialAuth = { user: null };

interface ConvexClientProviderProps {
  children: ReactNode;
  initialAuth?: InitialAuth;
  expectAuth?: boolean;
}

export function ConvexClientProvider({
  children,
  initialAuth,
  expectAuth = false,
}: ConvexClientProviderProps) {
  const isPublic = initialAuth === undefined;
  return (
    <AuthKitProvider
      initialAuth={initialAuth ?? SIGNED_OUT}
      onSessionExpired={isPublic ? false : undefined}
    >
      <ConvexProviderWithAuth
        client={convexClientFor(expectAuth)}
        useAuth={authHookFor(isPublic, expectAuth)}
      >
        <ViewAsBanner />
        <ErrorTrackingUserSync />
        <AnalyticsUserSync />
        <CartMerger />
        <ReportIssueProvider />
        <GlobalErrorBanner />
        <ConvexQueryCacheProvider>{children}</ConvexQueryCacheProvider>
      </ConvexProviderWithAuth>
    </AuthKitProvider>
  );
}
